import React, { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Search, Sprout, Wallet } from 'lucide-react';
import ExportButton from '../../components/common/ExportButton';
import FilterSelect from '../../components/common/FilterSelect';
import { produceTrackerAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useStoreContextStore } from '../../store/storeContextStore';
import { downloadExcelWorkbook } from '../../utils/exportExcel';
import {
  buildProduceReportRows,
  calculateProduceSummary,
} from '../../utils/produceInventoryTracker';

const todayKey = () => new Date().toISOString().split('T')[0];
const shiftDate = (value, days) => {
  const date = new Date(`${value || todayKey()}T00:00:00`);
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
};

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'completed', label: 'Completed' },
  { value: 'draft', label: 'In Progress' },
];

const VARIANCE_OPTIONS = [
  { value: 'all', label: 'All Variance' },
  { value: 'valid', label: 'No Variance' },
  { value: 'warning', label: 'Variance' },
];

const displayCurrency = (value) => `Nrs ${Number(value || 0).toLocaleString('en-NP', { maximumFractionDigits: 2 })}`;
const buildRecordSearchText = (record = {}) => [
  record.date,
  record.storeName,
  record.status,
  record.updatedBy,
].join(' ').toLowerCase();
const buildRowSearchText = (record = {}, row = {}) => [
  buildRecordSearchText(record),
  row.productName,
  row.sku,
].join(' ').toLowerCase();

const ProduceInventoryTrackerReport = () => {
  const { hasFeatureAccess } = useAuthStore();
  const selectedStore = useStoreContextStore((state) => state.selectedStore);
  const canViewPage = hasFeatureAccess('produce_tracker.report');
  const selectedStoreId = selectedStore?.storeInfoId || selectedStore?.store_info_id || selectedStore?.legacyStoreId || selectedStore?.legacy_store_id || '';
  const [records, setRecords] = useState([]);
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const [statusFilter, setStatusFilter] = useState('all');
  const [varianceFilter, setVarianceFilter] = useState('all');
  const [fromDate, setFromDate] = useState(shiftDate(todayKey(), -7));
  const [toDate, setToDate] = useState(todayKey());

  useEffect(() => {
    if (!canViewPage) return;

    const loadRecords = async () => {
      try {
        if (!selectedStoreId) {
          setRecords([]);
          return;
        }
        const params = {
          fromDate,
          toDate,
          storeId: selectedStoreId,
        };
        if (statusFilter !== 'all') params.status = statusFilter;

        const response = await produceTrackerAPI.getReport(params);
        setRecords(response.data?.data || []);
      } catch (error) {
        setRecords([]);
      }
    };

    loadRecords();
  }, [canViewPage, fromDate, selectedStoreId, statusFilter, toDate]);

  const filteredRecords = useMemo(() => {
    const query = deferredSearch.trim().toLowerCase();
    const from = fromDate ? new Date(`${fromDate}T00:00:00`).getTime() : null;
    const to = toDate ? new Date(`${toDate}T23:59:59`).getTime() : null;
    return records.flatMap((record) => {
      if (selectedStoreId && String(record.storeId || '') !== String(selectedStoreId)) return [];
      if (statusFilter !== 'all' && record.status !== statusFilter) return [];

      const recordTime = new Date(`${record.date}T00:00:00`).getTime();
      if (Number.isFinite(from) && recordTime < from) return [];
      if (Number.isFinite(to) && recordTime > to) return [];

      if (!query) return [record];

      const parentMatches = buildRecordSearchText(record).includes(query);
      if (parentMatches) {
        return [record];
      }

      const matchingRows = (record.rows || []).filter((row) => buildRowSearchText(record, row).includes(query));
      if (!matchingRows.length) {
        return [];
      }

      return [{
        ...record,
        rows: matchingRows,
      }];
    });
  }, [deferredSearch, fromDate, records, selectedStoreId, statusFilter, toDate]);

  const reportRows = useMemo(() => {
    const baseRows = buildProduceReportRows(filteredRecords);
    return baseRows.filter((row) => varianceFilter === 'all' || row.varianceStatus === varianceFilter);
  }, [filteredRecords, varianceFilter]);

  const visibleRecords = useMemo(
    () => Array.from(new Map(reportRows.map((row) => [row.recordId, row])).values()),
    [reportRows]
  );

  const summary = useMemo(() => {
    const rowSummary = calculateProduceSummary(reportRows);
    return {
      totalRecords: visibleRecords.length,
      totalRows: reportRows.length,
      purchaseSubtotal: reportRows.reduce((sum, row) => sum + Number(row.purchaseTotal || 0), 0),
      totalVariance: rowSummary.varianceTotal,
      varianceCount: rowSummary.varianceCount,
      ...rowSummary,
    };
  }, [reportRows, visibleRecords.length]);

  const exportExcel = () => {
    downloadExcelWorkbook({
      fileName: `produce-inventory-report-${fromDate}-to-${toDate}.xlsx`,
      sheetName: 'Produce Inventory',
      columns: [
        { header: 'Date', key: 'date', width: 14 },
        { header: 'Store', key: 'storeName', width: 20 },
        { header: 'Status', key: 'status', width: 14 },
        { header: 'Product', key: 'productName', width: 26 },
        { header: 'SKU', key: 'sku', width: 18 },
        { header: 'Opening Qty', key: 'openingQuantity', width: 14 },
        { header: 'Opening Rate', key: 'openingRate', width: 14 },
        { header: 'Opening Total', key: 'openingTotal', width: 14 },
        { header: 'Purchase Qty', key: 'purchaseQuantity', width: 14 },
        { header: 'Purchase Rate', key: 'purchaseRate', width: 14 },
        { header: 'Purchase Total', key: 'purchaseTotal', width: 14 },
        { header: 'Sales Qty', key: 'salesQuantity', width: 12 },
        { header: 'Closing Qty', key: 'closingQuantity', width: 14 },
        { header: 'Closing Rate', key: 'closingRate', width: 14 },
        { header: 'Closing Total', key: 'closingTotal', width: 14 },
        { header: 'Wastage Qty', key: 'wastageQuantity', width: 14 },
        { header: 'Wastage Rate', key: 'wastageRate', width: 14 },
        { header: 'Wastage Total', key: 'wastageTotal', width: 14 },
        { header: 'Expected Closing', key: 'expectedClosing', width: 16 },
        { header: 'Variance', key: 'variance', width: 12 },
        { header: 'Updated By', key: 'updatedBy', width: 18 },
        { header: 'Updated At', key: 'updatedAt', width: 22 },
      ],
      rows: reportRows,
    });
  };

  if (!canViewPage) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Produce Inventory Tracker Report</h1>
        <div className="card">
          <p className="text-gray-500">Access denied.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 border-b border-slate-200 pb-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-[24px] font-bold tracking-tight text-slate-900">Produce Inventory Tracker Report</h1>
          <p className="mt-1 max-w-3xl text-sm text-slate-600">
            Review daily produce movement, expected closing, wastage impact, and variance by product and store.
          </p>
        </div>
        <ExportButton onClick={exportExcel} />
      </div>

      <div className="grid gap-4 lg:grid-cols-6">
        <div className="card border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(125,211,252,0.18),_transparent_42%),linear-gradient(135deg,_#f8fafc_0%,_#ffffff_48%,_#ecfeff_100%)]">
          <div className="flex items-center gap-3">
            <Sprout className="h-6 w-6 text-cyan-700" />
            <div>
              <p className="text-sm font-medium text-slate-700">Tracker Days</p>
              <p className="text-2xl font-bold text-slate-900">{summary.totalRecords}</p>
              <p className="mt-1 text-xs text-slate-500">{summary.totalRows} product rows</p>
            </div>
          </div>
        </div>
        <div className="card border border-slate-200 bg-slate-50">
          <div className="flex items-center gap-3">
            <Wallet className="h-6 w-6 text-slate-600" />
            <div>
              <p className="text-sm font-medium text-slate-800">Opening SKU</p>
              <p className="text-2xl font-bold text-slate-900">{summary.openingSkuCount}</p>
              <p className="mt-1 text-xs text-slate-600">Qty: {summary.openingUnits}</p>
              <p className="mt-1 text-xs text-slate-600">Amount: {displayCurrency(summary.openingAmount)}</p>
            </div>
          </div>
        </div>
        <div className="card border border-sky-200 bg-sky-50">
          <div className="flex items-center gap-3">
            <Wallet className="h-6 w-6 text-sky-600" />
            <div>
              <p className="text-sm font-medium text-sky-800">Purchased SKU</p>
              <p className="text-2xl font-bold text-sky-700">{summary.purchasedSkuCount}</p>
              <p className="mt-1 text-xs text-sky-700">Qty: {summary.purchasedUnits}</p>
              <p className="mt-1 text-xs text-sky-700">Amount: {displayCurrency(summary.purchasedAmount)}</p>
            </div>
          </div>
        </div>
        <div className="card border border-emerald-200 bg-emerald-50">
          <div className="flex items-center gap-3">
            <Wallet className="h-6 w-6 text-emerald-600" />
            <div>
              <p className="text-sm font-medium text-emerald-800">Closing SKU</p>
              <p className="text-2xl font-bold text-emerald-700">{summary.closingSkuCount}</p>
              <p className="mt-1 text-xs text-emerald-700">Qty: {summary.closingUnits}</p>
              <p className="mt-1 text-xs text-emerald-700">Amount: {displayCurrency(summary.closingAmount)}</p>
            </div>
          </div>
        </div>
        <div className="card border border-amber-200 bg-amber-50">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-amber-600" />
            <div>
              <p className="text-sm font-medium text-amber-800">Wastage SKU</p>
              <p className="text-2xl font-bold text-amber-700">{summary.wastageSkuCount}</p>
              <p className="mt-1 text-xs text-amber-700">Qty: {summary.wastageUnits}</p>
              <p className="mt-1 text-xs text-amber-700">Amount: {displayCurrency(summary.wastageAmount)}</p>
            </div>
          </div>
        </div>
        <div className={`card ${summary.totalVariance === 0 ? 'border border-emerald-200 bg-emerald-50' : 'border border-rose-200 bg-rose-50'}`}>
          <div className="flex items-center gap-3">
            {summary.totalVariance !== 0 ? <AlertTriangle className="h-6 w-6 text-rose-600" /> : <Sprout className="h-6 w-6 text-emerald-600" />}
            <div>
              <p className={`text-sm font-medium ${summary.totalVariance === 0 ? 'text-emerald-800' : 'text-rose-800'}`}>Total Variance</p>
              <p className={`text-2xl font-bold ${summary.totalVariance === 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{summary.totalVariance}</p>
              <p className={`mt-1 text-xs ${summary.totalVariance === 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                Purchase Amount: {displayCurrency(summary.purchaseSubtotal)}
              </p>
              <p className={`mt-1 text-xs ${summary.totalVariance === 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                Variance Rows: {summary.varianceCount}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="flex flex-wrap items-end gap-3">
          <label className="relative block min-w-[240px] flex-[1.1_1_300px]">
            <span className="label">Search</span>
            <Search className="pointer-events-none absolute left-3 top-[2.55rem] h-4 w-4 text-gray-400" />
            <input
              className="input pl-10"
              placeholder="Store, product, SKU, user..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>

          <FilterSelect
            label="Status"
            value={statusFilter}
            options={STATUS_OPTIONS}
            onChange={setStatusFilter}
            className="w-[150px]"
          />

          <FilterSelect
            label="Variance"
            value={varianceFilter}
            options={VARIANCE_OPTIONS}
            onChange={setVarianceFilter}
            className="w-[150px]"
          />

          <div className="w-[140px]">
            <label className="label">Date From</label>
            <input className="input text-sm" type="date" value={fromDate} max={toDate} onChange={(event) => setFromDate(event.target.value)} />
          </div>
          <div className="w-[140px]">
            <label className="label">Date To</label>
            <input className="input text-sm" type="date" value={toDate} min={fromDate} max={todayKey()} onChange={(event) => setToDate(event.target.value)} />
          </div>
        </div>
      </div>

      <div className="card table-scroll-shell">
        <table className="min-w-[1880px] divide-y divide-slate-200 text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-3 py-3 font-semibold">Date</th>
              <th className="px-3 py-3 font-semibold">Store</th>
              <th className="px-3 py-3 font-semibold">Status</th>
              <th className="px-3 py-3 font-semibold">Product</th>
              <th className="px-3 py-3 font-semibold">SKU</th>
              <th className="px-3 py-3 font-semibold text-right">Opening Qty</th>
              <th className="px-3 py-3 font-semibold text-right">Opening Rate</th>
              <th className="px-3 py-3 font-semibold text-right">Purchase Qty</th>
              <th className="px-3 py-3 font-semibold text-right">Purchase Rate</th>
              <th className="px-3 py-3 font-semibold text-right">Sales Qty</th>
              <th className="px-3 py-3 font-semibold text-right">Closing Qty</th>
              <th className="px-3 py-3 font-semibold text-right">Closing Rate</th>
              <th className="px-3 py-3 font-semibold text-right">Wastage Qty</th>
              <th className="px-3 py-3 font-semibold text-right">Wastage Rate</th>
              <th className="px-3 py-3 font-semibold text-right">Expected Closing</th>
              <th className="px-3 py-3 font-semibold text-right">Variance</th>
              <th className="px-3 py-3 font-semibold">Updated</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {reportRows.map((row) => (
              <tr key={`${row.recordId}-${row.sku || row.productName}`} className={row.variance === 0 ? 'hover:bg-emerald-50/40' : 'bg-rose-50/30 hover:bg-rose-50/60'}>
                <td className="px-3 py-3 text-slate-700">{row.date}</td>
                <td className="px-3 py-3 text-slate-700">{row.storeName}</td>
                <td className="px-3 py-3">
                  <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${row.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                    {row.status === 'completed' ? 'Completed' : 'In Progress'}
                  </span>
                </td>
                <td className="px-3 py-3 font-medium text-slate-900">{row.productName}</td>
                <td className="px-3 py-3 text-slate-500">{row.sku || '-'}</td>
                <td className="px-3 py-3 text-right">{row.openingQuantity}</td>
                <td className="px-3 py-3 text-right">{displayCurrency(row.openingRate)}</td>
                <td className="px-3 py-3 text-right">{row.purchaseQuantity}</td>
                <td className="px-3 py-3 text-right">{displayCurrency(row.purchaseRate)}</td>
                <td className="px-3 py-3 text-right">{row.salesQuantity}</td>
                <td className="px-3 py-3 text-right">{row.closingQuantity}</td>
                <td className="px-3 py-3 text-right">{displayCurrency(row.closingRate)}</td>
                <td className="px-3 py-3 text-right">{row.wastageQuantity}</td>
                <td className="px-3 py-3 text-right">{displayCurrency(row.wastageRate)}</td>
                <td className="px-3 py-3 text-right">{row.expectedClosing}</td>
                <td className={`px-3 py-3 text-right font-semibold ${row.variance === 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                  {row.variance}
                </td>
                <td className="px-3 py-3 text-slate-500">
                  <div>{row.updatedBy || '-'}</div>
                  <div className="mt-0.5 text-xs">{row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '-'}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {!reportRows.length ? (
          <div className="px-4 py-12 text-center text-sm text-slate-500">
            No produce inventory rows found for the selected filters.
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ProduceInventoryTrackerReport;
